import { contractAddress, ERC20, Pool } from "@verified-network/verified-sdk";
import { ethers, BigNumber } from "ethers";
import { errorToast, successToast } from "../../utils/constants";
import { ChainConfig } from "../../utils/config";
import ERC20ABI from "../../utils/abis/ERC20.json";
import BALANCERABI from "../../utils/abis/Balancer.json";
import { handleCustodyTransaction } from "../../utils/helpers/hextrust";

export const createSecondaryAndMarginOrder = async (
  paymentToken,
  chainId,
  userAddress,
  signer,
  poolId,
  tokenIn,
  tokenOut,
  tokenOutSymbol,
  tokenOutDecimals,
  tokensList,
  amountInWei,
  priceWei,
  isLimit,
  provider,
  web3,
  custodyWalletDetails,
) => {
  try {
    const abiCoder = new ethers.utils.AbiCoder();
    const balancerVaultAddress = contractAddress.balancerVault;
    let limitArr, userData, tokenInIndex, tokenOutIndex;

    if (isLimit) {
      userData = abiCoder.encode(
        ["bytes32", "uint"],
        [ethers.utils.formatBytes32String("Limit"), priceWei],
      );
    } else {
      userData = abiCoder.encode([], []);
    }
    tokenInIndex = Number(tokensList.indexOf(tokenIn));
    tokenOutIndex = Number(tokensList?.indexOf(tokenOut));
    limitArr = new Array(tokensList?.length).fill("0");
    limitArr[tokenInIndex] = amountInWei.toString();
    const tokenInContract = new ERC20(signer, tokenIn);
    const tokenOutContract = new ERC20(signer, tokenOut);
    const tokenOutBalance = Number(
      await tokenOutContract
        .balanceOf(userAddress)
        .then((res) => res?.response?.result[0]),
    );

    let isApproved = false;

    if (balancerVaultAddress) {
      await tokenInContract
        .allowance(userAddress, balancerVaultAddress)
        .then((res) => {
          if (Number(res?.response?.result[0]) >= Number(amountInWei)) {
            isApproved = true;
          }
        });
    }

    if (
      custodyWalletDetails &&
      Object.keys(custodyWalletDetails)?.length > 0 &&
      custodyWalletDetails?.custodyId?.length > 0 &&
      custodyWalletDetails?.address &&
      custodyWalletDetails?.chainId > 0 &&
      web3 &&
      provider &&
      balancerVaultAddress
    ) {
      if (!isApproved && tokenIn) {
        const tokenInContractWeb3 = new web3.eth.Contract(ERC20ABI, tokenIn);
        const txData = tokenInContractWeb3.methods
          .approve(balancerVaultAddress, Number(amountInWei))
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          tokenIn,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-approve",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            isApproved = true;

            successToast(
              "Transaction Successful!",
              "Approved Vault Successfully",
            );
          } else {
            isApproved = false;
            errorToast(
              "Oops something went wrong",
              "Approve Transaction Failed",
            );
          }
        });
      }

      if (isApproved && userAddress) {
        const balancerVaultContractWeb3 = new web3.eth.Contract(
          BALANCERABI,
          balancerVaultAddress,
        );

        const txData = balancerVaultContractWeb3.methods
          .batchSwap(
            "0",
            [
              {
                poolId: poolId,
                assetInIndex: Number(tokenInIndex),
                assetOutIndex: Number(tokenOutIndex),
                amount: Number(amountInWei),
                userData: userData,
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr?.map((lim) => Number(lim)),
            Number("999999999999999999"),
          )
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          balancerVaultAddress,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-batchSwap",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            successToast(
              "Transaction Successful!",
              "Order Created Successfully",
            );
          } else {
            errorToast(
              "Oops Something went wrong",
              "Order Transaction Failed!",
            );
          }
        });
      }
    } else {
      if (!isApproved && balancerVaultAddress) {
        await tokenInContract
          .approve(
            balancerVaultAddress,
            amountInWei.toString(),
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then((res) => {
            if (res?.status === 0) {
              isApproved = true;

              successToast(
                "Transaction Successful!",
                "Approved Vault Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );
              console.log(
                "Successful approval with hash: ",
                res?.response?.hash,
              );
            } else {
              isApproved = false;
              errorToast(
                "Oops something went wrong",
                "Approve Transaction Failed",
              );

              console.error("approval failed");
            }
          })
          .catch((err) => {
            isApproved = false;
            errorToast(
              "Oops something went wrong",
              "Approve Transaction Failed",
            );
            console.error("approval failed with error: ", err);
          });
      }

      if (isApproved && userAddress && signer) {
        const balancerVaultContract = new Pool(signer);
        await balancerVaultContract
          .batchSwap(
            "0",
            [
              {
                poolId: poolId,
                assetInIndex: tokenInIndex.toString(),
                assetOutIndex: tokenOutIndex.toString(),
                amount: amountInWei.toString(),
                userData: userData,
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr,
            "999999999999999999", // Infinity
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then(async (res) => {
            if (res?.status === 0) {
              successToast(
                "Transaction Successful!",
                "Order Created Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              console.log(
                "successful batch swap with hash: ",
                res?.response?.hash,
              );
            } else {
              errorToast(
                "Oops Something went wrong",
                "Order Transaction Failed!",
              );
              console.error("batchSwap failed");
            }
          })
          .catch((err) => {
            errorToast(
              "Oops Something went wrong",
              "Order Transaction Failed!",
            );
            console.error("batchSwap failed with error: ", err);
          });
      }
    }
  } catch (err) {
    errorToast("Oops Something went wrong", "Order Transaction Failed!");
    console.error("batchSwap failed with error: ", err);
  }
};

export const createCustomSecondaryAndMarginOrder = async (
  provider,
  web3,
  custodyWalletDetails,
  paymentToken,
  userAddress,
  signer,
  chainId,
  poolId,
  tokenIn,
  tokenOut,
  tokenOutSymbol,
  tokenOutDecimals,
  tokensList,
  amountInWei,
  returningAmount,
  userData,
  isSecondary,
  isCancelOrder,
) => {
  try {
    const balancerVaultAddress = contractAddress.balancerVault;
    const managerAddress =
      contractAddress[chainId].BalancerSecondaryIssueManager;
    let limitArr, tokenInIndex, tokenOutIndex;
    tokenInIndex = Number(tokensList.indexOf(tokenIn));
    tokenOutIndex = Number(tokensList?.indexOf(tokenOut));
    limitArr = new Array(tokensList.length).fill("0");
    limitArr[tokenInIndex] = amountInWei.toString();
    const tokenInContract = new ERC20(signer, tokenIn);
    const tokenOutContract = new ERC20(signer, tokenOut);
    const tokenOutBalance = Number(
      await tokenOutContract
        .balanceOf(userAddress)
        .then((res) => res?.response?.result[0]),
    );
    let isApproved = false;
    let firstApprovalFailed = false;
    let isManagerApproved = false;
    let secondApprovalFailed = false;
    if (balancerVaultAddress) {
      await tokenInContract
        .allowance(userAddress, balancerVaultAddress)
        .then((res) => {
          if (Number(res?.response?.result[0]) >= Number(amountInWei)) {
            isApproved = true;
          }
        });
    }
    if (isSecondary && managerAddress) {
      await tokenOutContract
        .allowance(userAddress, managerAddress)
        .then((res) => {
          if (Number(res?.response?.result[0]) >= Number(returningAmount)) {
            isManagerApproved = true;
          }
        });
    }

    if (
      custodyWalletDetails &&
      Object.keys(custodyWalletDetails)?.length > 0 &&
      custodyWalletDetails?.custodyId?.length > 0 &&
      custodyWalletDetails?.address &&
      custodyWalletDetails?.chainId > 0 &&
      web3 &&
      provider &&
      balancerVaultAddress
    ) {
      if (!isApproved && tokenIn) {
        const tokenInContractWeb3 = new web3.eth.Contract(ERC20ABI, tokenIn);
        const txData = tokenInContractWeb3.methods
          .approve(balancerVaultAddress, Number(amountInWei))
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          tokenIn,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-approve",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            if (!isSecondary) {
              isApproved = true;
            }
            successToast(
              "Transaction Successful!",
              "Approved Vault Successfully",
            );
          } else {
            firstApprovalFailed = true;
            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );
            console.error("approval failed");
          }
        });
      }

      if (
        isSecondary &&
        !isManagerApproved &&
        !firstApprovalFailed &&
        !isCancelOrder &&
        tokenOut
      ) {
        const tokenOutContractWeb3 = new web3.eth.Contract(ERC20ABI, tokenOut);
        const txData = tokenOutContractWeb3.methods
          .approve(managerAddress, Number(returningAmount))
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          tokenIn,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-approve",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            isApproved = true;
            successToast(
              "Transaction Successful!",
              "Approved Manager Successfully",
            );
          } else {
            secondApprovalFailed = true;

            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );
            console.error("approve manager failed");
          }
        });
      }

      if (
        isApproved &&
        !firstApprovalFailed &&
        !secondApprovalFailed &&
        userAddress
      ) {
        const balancerVaultContractWeb3 = new web3.eth.Contract(
          BALANCERABI,
          balancerVaultAddress,
        );

        const txData = balancerVaultContractWeb3.methods
          .batchSwap(
            0,
            [
              {
                poolId: poolId,
                assetInIndex: Number(tokenInIndex.toString()),
                assetOutIndex: Number(tokenOutIndex),
                amount: Number(amountInWei),
                userData: userData,
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr?.map((lim) => Number(lim)),
            Number("999999999999999999"),
          )
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          balancerVaultAddress,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-batchSwap",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            successToast(
              "Transaction Successful!",
              "Order Settled Successfully",
            );
          } else {
            errorToast(
              "Oops Something went wrong",
              "Settle Order Transaction Failed!",
            );
          }
        });
      }
    } else {
      if (!isApproved && balancerVaultAddress) {
        await tokenInContract
          .approve(
            balancerVaultAddress,
            amountInWei.toString(),
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then((res) => {
            if (res?.status === 0) {
              if (!isSecondary) {
                isApproved = true;
              }
              successToast(
                "Transaction Successful!",
                "Approved Vault Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );
              console.log(
                "Successful approval with hash: ",
                res?.response?.hash,
              );
            } else {
              firstApprovalFailed = true;
              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );
              console.error("approval failed");
            }
          })
          .catch((err) => {
            firstApprovalFailed = true;
            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );
            console.error("approval failed with error: ", err);
          });
      }

      if (
        isSecondary &&
        !isManagerApproved &&
        !firstApprovalFailed &&
        !isCancelOrder
      ) {
        await tokenOutContract
          .approve(
            managerAddress,
            returningAmount.toString(),
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then((res) => {
            if (res?.status === 0) {
              isApproved = true;
              successToast(
                "Transaction Successful!",
                "Approved Manager Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              console.log(
                "Successful manager approval with hash: ",
                res?.response?.hash,
              );
            } else {
              secondApprovalFailed = true;

              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );
              console.error("approve manager failed");
            }
          })
          .catch((err) => {
            secondApprovalFailed = true;
            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );
            console.error("approve manager failed with error: ", err);
          });
      }

      if (
        isApproved &&
        !firstApprovalFailed &&
        !secondApprovalFailed &&
        userAddress &&
        signer
      ) {
        const balancerVaultContract = new Pool(signer);
        await balancerVaultContract
          .batchSwap(
            "0",
            [
              {
                poolId: poolId,
                assetInIndex: tokenInIndex.toString(),
                assetOutIndex: tokenOutIndex.toString(),
                amount: amountInWei.toString(),
                userData: userData,
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr,
            "999999999999999999", // Infinity
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then(async (res) => {
            if (res?.status === 0) {
              successToast(
                "Transaction Successful!",
                "Order Settled Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              console.log(
                "successful batch swap with hash: ",
                res?.response?.hash,
              );
            } else {
              errorToast(
                "Oops Something went wrong",
                "Settle Order Transaction Failed!",
              );
              console.error("batchSwap failed");
            }
          })
          .catch((err) => {
            errorToast(
              "Oops Something went wrong",
              "Settle Order Transaction Failed!",
            );

            console.error("batchSwap failed with error: ", err);
          });
      }
    }
  } catch (err) {
    errorToast("Oops Something went wrong", "Transaction Failed!");
    console.error("batchSwap failed with error: ", err);
  }
};

export const createPrimaryOrder = async (
  provider,
  web3,
  custodyWalletDetails,
  paymentToken,
  chainId,
  userAddress,
  signer,
  poolId,
  tokenIn,
  tokenOut,
  tokenOutSymbol,
  tokenOutDecimals,
  tokensList,
  amountInWei,
  calculatedAmount,
  issueManagerAddress,
  orderType,
) => {
  try {
    const balancerVaultAddress = contractAddress.balancerVault;
    let limitArr, tokenInIndex, tokenOutIndex;
    tokenInIndex = Number(tokensList.indexOf(tokenIn));
    tokenOutIndex = Number(tokensList?.indexOf(tokenOut));
    limitArr = new Array(tokensList.length).fill("0");
    limitArr[tokenInIndex] = amountInWei.toString();
    const tokenInContract = new ERC20(signer, tokenIn);
    const tokenOutContract = new ERC20(signer, tokenOut);
    const tokenOutBalance = Number(
      await tokenOutContract
        .balanceOf(userAddress)
        .then((res) => res?.response?.result[0]),
    );
    let tokenInAllowance, tokenOutAllowance;

    let isApproved = false;
    let firstApprovalFailed = false;
    if (balancerVaultAddress && issueManagerAddress) {
      tokenInAllowance = await tokenInContract
        .allowance(userAddress, balancerVaultAddress)
        .then((res) => res?.response?.result[0]);
      tokenOutAllowance = await tokenOutContract
        .allowance(userAddress, issueManagerAddress)
        .then((res) => res?.response?.result[0]);
    }

    if (
      custodyWalletDetails &&
      Object.keys(custodyWalletDetails)?.length > 0 &&
      custodyWalletDetails?.custodyId?.length > 0 &&
      custodyWalletDetails?.address &&
      custodyWalletDetails?.chainId > 0 &&
      web3 &&
      provider &&
      balancerVaultAddress
    ) {
      if (orderType === "buy") {
        if (Number(tokenInAllowance) < Number(calculatedAmount) && tokenIn) {
          const tokenInContractWeb3 = new web3.eth.Contract(ERC20ABI, tokenIn);
          const txData = tokenInContractWeb3.methods
            .approve(balancerVaultAddress, Number(calculatedAmount))
            .encodeABI();
          const txNonce = await web3.eth.getTransactionCount(
            custodyWalletDetails?.address,
          );
          await handleCustodyTransaction(
            custodyWalletDetails,
            tokenIn,
            provider,
            "0",
            txData,
            web3.utils.toHex(txNonce),
            "Verified-Network-approve",
          ).then(async (cusRes) => {
            if (cusRes?.toLowerCase() === "completed") {
              successToast(
                "Transaction Successful!",
                "Approved Vault Successfully",
              );
            } else {
              firstApprovalFailed = true;
              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );
            }
          });
        }
        //handle approval for refunding investment by DP
        //no need for conditional approval it will always increase allowance??
        if (!firstApprovalFailed && tokenOut) {
          const tokenOutContractWeb3 = new web3.eth.Contract(
            ERC20ABI,
            tokenOut,
          );
          const txData = tokenOutContractWeb3.methods
            .approve(
              issueManagerAddress,
              Number(tokenOutAllowance) + Number(amountInWei),
            )
            .encodeABI();
          const txNonce = await web3.eth.getTransactionCount(
            custodyWalletDetails?.address,
          );
          await handleCustodyTransaction(
            custodyWalletDetails,
            tokenOut,
            provider,
            "0",
            txData,
            web3.utils.toHex(txNonce),
            "Verified-Network-approve",
          ).then(async (cusRes) => {
            if (cusRes?.toLowerCase() === "completed") {
              isApproved = true;
              successToast(
                "Transaction Successful!",
                "Approved Manager Successfully",
              );
            } else {
              errorToast(
                "Oops Something went wrong",
                "Approve Manager Transaction Failed!",
              );
            }
          });
        }
      }

      if (
        orderType === "sell" &&
        Number(tokenInAllowance) < Number(amountInWei) &&
        tokenIn
      ) {
        const tokenInContractWeb3 = new web3.eth.Contract(ERC20ABI, tokenIn);
        const txData = tokenInContractWeb3.methods;
        tokenInContract.approve(balancerVaultAddress, amountInWei).encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          tokenIn,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-approve",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            successToast(
              "Transaction Successful!",
              "Approved Vault Successfully",
            );

            isApproved = true;
          } else {
            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );
          }
        });
      } else {
        isApproved = true;
      }

      if (isApproved && userAddress) {
        const balancerVaultContractWeb3 = new web3.eth.Contract(
          BALANCERABI,
          balancerVaultAddress,
        );

        const txData = balancerVaultContractWeb3.methods
          .batchSwap(
            orderType === "sell" ? 0 : 1,
            [
              {
                poolId: poolId,
                assetInIndex: Number(tokenInIndex),
                assetOutIndex: Number(tokenOutIndex),
                amount: Number(amountInWei),
                userData: "0x",
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr?.map((lim) => Number(lim)),
            Number("999999999999999999"),
          )
          .encodeABI();
        const txNonce = await web3.eth.getTransactionCount(
          custodyWalletDetails?.address,
        );
        await handleCustodyTransaction(
          custodyWalletDetails,
          balancerVaultAddress,
          provider,
          "0",
          txData,
          web3.utils.toHex(txNonce),
          "Verified-Network-batchSwap",
        ).then(async (cusRes) => {
          if (cusRes?.toLowerCase() === "completed") {
            successToast(
              "Transaction Successful!",
              "Order Created Successfully",
            );
          } else {
            errorToast(
              "Oops Something went wrong",
              "Order Transaction Failed!",
            );
          }
        });

        const balancerVaultContract = new Pool(signer);
        await balancerVaultContract
          .batchSwap(
            orderType === "sell" ? "0" : "1",
            [
              {
                poolId: poolId,
                assetInIndex: tokenInIndex.toString(),
                assetOutIndex: tokenOutIndex.toString(),
                amount: amountInWei.toString(),
                userData: "0x",
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr,
            "999999999999999999", // Infinity
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then(async (res) => {
            if (res?.status === 0) {
              successToast(
                "Transaction Successful!",
                "Order Created Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              console.log(
                "successful batch swap with hash: ",
                res?.response?.hash,
              );
            } else {
              errorToast(
                "Oops Something went wrong",
                "Order Transaction Failed!",
              );
              console.error("batchSwap failed");
            }
          })
          .catch((err) => {
            errorToast(
              "Oops Something went wrong",
              "Order Transaction Failed!",
            );
            console.error("batchSwap failed with error: ", err);
          });
      }
    } else {
      if (orderType === "buy") {
        if (Number(tokenInAllowance) < Number(calculatedAmount)) {
          await tokenInContract
            .approve(
              balancerVaultAddress,
              calculatedAmount.toString(),
              paymentToken?.address
                ? {
                    paymentToken: paymentToken?.address,
                    apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                    rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                  }
                : {},
            )
            .then((res) => {
              if (res?.status === 0) {
                successToast(
                  "Transaction Successful!",
                  "Approved Vault Successfully",
                  `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
                );
              } else {
                firstApprovalFailed = true;
                errorToast(
                  "Oops Something went wrong",
                  "Approve Transaction Failed!",
                );
                console.error("vault approval failed");
              }
            })
            .catch((err) => {
              firstApprovalFailed = true;
              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );
              console.error("vault approval failed with error: ", err);
            });
        }
        //handle approval for refunding investment by DP
        //no need for conditional approval it will always increase allowance??
        if (!firstApprovalFailed) {
          await tokenOutContract
            .approve(
              issueManagerAddress,
              BigNumber.from(tokenOutAllowance)
                .add(BigNumber.from(amountInWei))
                .toString(),
              paymentToken?.address
                ? {
                    paymentToken: paymentToken?.address,
                    apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                    rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                  }
                : {},
            )
            .then((res) => {
              if (res?.status === 0) {
                isApproved = true;
                successToast(
                  "Transaction Successful!",
                  "Approved Manager Successfully",
                  `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
                );
              } else {
                errorToast(
                  "Oops Something went wrong",
                  "Approve Manager Transaction Failed!",
                );
                console.error("manager approval failed");
              }
            })
            .catch((err) => {
              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );
              console.error("manager approval failed with error: ", err);
            });
        }
      }

      if (
        orderType === "sell" &&
        Number(tokenInAllowance) < Number(amountInWei)
      ) {
        await tokenInContract
          .approve(
            balancerVaultAddress,
            amountInWei.toString(),
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then((res) => {
            if (res?.status === 0) {
              successToast(
                "Transaction Successful!",
                "Approved Vault Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              isApproved = true;
            } else {
              errorToast(
                "Oops Something went wrong",
                "Approve Transaction Failed!",
              );

              console.error("vault approval failed");
            }
          })
          .catch((err) => {
            errorToast(
              "Oops Something went wrong",
              "Approve Transaction Failed!",
            );

            console.error("vault approval failed with error: ", err);
          });
      } else {
        isApproved = true;
      }

      if (isApproved && userAddress && signer) {
        const balancerVaultContract = new Pool(signer);
        await balancerVaultContract
          .batchSwap(
            orderType === "sell" ? "0" : "1",
            [
              {
                poolId: poolId,
                assetInIndex: tokenInIndex.toString(),
                assetOutIndex: tokenOutIndex.toString(),
                amount: amountInWei.toString(),
                userData: "0x",
              },
            ],
            tokensList,
            {
              fromInternalBalance: false,
              recipient: userAddress,
              sender: userAddress,
              toInternalBalance: false,
            },
            limitArr,
            "999999999999999999", // Infinity
            paymentToken?.address
              ? {
                  paymentToken: paymentToken?.address,
                  apiKey: process.env.REACT_APP_BICONOMY_MEE_KEY,
                  rpcUrl: ChainConfig[chainId]?.rpcUrls[0],
                }
              : {},
          )
          .then(async (res) => {
            if (res?.status === 0) {
              successToast(
                "Transaction Successful!",
                "Order Created Successfully",
                `${ChainConfig[chainId]?.blockExplorerUrls[0]}tx/${res?.response?.hash}`,
              );

              console.log(
                "successful batch swap with hash: ",
                res?.response?.hash,
              );
            } else {
              errorToast(
                "Oops Something went wrong",
                "Order Transaction Failed!",
              );
              console.error("batchSwap failed");
            }
          })
          .catch((err) => {
            errorToast(
              "Oops Something went wrong",
              "Order Transaction Failed!",
            );
            console.error("batchSwap failed with error: ", err);
          });
      }
    }
  } catch (err) {
    errorToast("Oops Something went wrong", "Order Transaction Failed!");

    console.error("batchSwap failed with error: ", err);
  }
};
